import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, ChevronDown, ChevronUp, CheckCircle, AlertTriangle, Clock, Package, Wrench, ClipboardList, BookOpen, RefreshCw, Save, ChevronRight, PlayCircle } from 'lucide-react';
import { useAdminAuth } from '../AuthContext';

const BASE = process.env.REACT_APP_BACKEND_URL;

async function apiFetch(token, path, opts = {}) {
  const res = await fetch(`${BASE}${path}`, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', ...opts.headers },
    ...opts,
  });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || `Error ${res.status}`); }
  if (res.status === 204) return null;
  return res.json();
}

const PROBLEM_REASONS = [
  'Material shortage — ran out of stock',
  'Material defect — stock rejected',
  'Machine breakdown / malfunction',
  'Tooling failure / broken bit',
  'Quality failure — out of spec',
  'Measurement error — needs re-work',
  'Setup issue — needs re-programming',
  'NFA compliance hold',
  'Other (see notes)',
];

const RUN_STATUS_CFG = {
  completed:    { label: 'Completed',     cls: 'text-green-400 bg-green-500/10 border-green-500/20' },
  in_progress:  { label: 'In Progress',   cls: 'text-blue-400 bg-blue-500/10 border-blue-500/20' },
  partial:      { label: 'Partial',       cls: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20' },
  has_problems: { label: 'Has Problems',  cls: 'text-red-400 bg-red-500/10 border-red-500/20' },
  pending:      { label: 'Pending Start', cls: 'text-zinc-400 bg-zinc-800 border-zinc-700' },
  no_runs:      { label: 'Not Started',   cls: 'text-zinc-600 bg-zinc-900 border-zinc-800' },
};

// ── Run card ──────────────────────────────────────────────────────────────────
function RunCard({ run, profile, onComplete, onProblem }) {
  const [expanded, setExpanded] = useState(false);
  const [opsChecked, setOpsChecked] = useState({});
  const isCompleted = run.status === 'completed';
  const hasProblem = run.status === 'problem';
  const isInventory = run.source === 'inventory_replenishment';
  const operations = profile?.operations || [];
  const matLen = profile?.material_length_per_unit_in || 4;
  const totalFt = ((matLen * run.quantity_planned) / 12).toFixed(2);

  return (
    <div data-testid={`shop-run-${run.run_id}`}
      className={`border transition-all ${isCompleted ? 'border-green-500/30 bg-green-500/3 opacity-70' : hasProblem ? 'border-red-500/30 bg-red-500/3' : run.nfa_item ? 'border-orange-500/30' : 'border-white/10'} bg-[#1a1a1a]`}>
      <button className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-white/2" onClick={() => setExpanded(!expanded)}>
        <div className={`w-8 h-8 flex items-center justify-center flex-shrink-0 ${isCompleted ? 'bg-green-500/20' : hasProblem ? 'bg-red-500/20' : 'bg-[#0f0f0f]'}`}>
          {isCompleted ? <CheckCircle size={16} className="text-green-400" /> : hasProblem ? <AlertTriangle size={16} className="text-red-400" /> : <Clock size={16} className="text-zinc-500" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bebas text-lg text-white tracking-wide">{run.product_name}</span>
            {run.nfa_item && <span className="flex items-center gap-1 font-inter text-[9px] font-bold text-orange-400 border border-orange-500/20 bg-orange-500/10 px-1.5 py-0.5"><Shield size={8} />NFA</span>}
            {isInventory && <span className="font-inter text-[9px] font-bold text-cyan-400 border border-cyan-500/20 bg-cyan-500/10 px-1.5 py-0.5">SHELF STOCK</span>}
            {profile?.machine_code && <span className="font-inter text-[9px] text-zinc-500 border border-white/10 px-1.5 py-0.5">{profile.machine_code}</span>}
          </div>
          <div className="flex items-center gap-4 mt-0.5">
            <span className="font-inter text-xs text-zinc-400">Build: <strong className="text-white">{run.quantity_planned} units</strong></span>
            {profile?.material_spec && <span className="font-inter text-xs text-zinc-500">Pull: <strong className="text-orange-400">{totalFt} ft</strong> of {profile.material_diameter_in}"Ø stock</span>}
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {!isCompleted && !hasProblem && (
            <>
              <button data-testid={`complete-run-${run.run_id}`}
                onClick={e => { e.stopPropagation(); onComplete(run); }}
                className="flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white font-bebas text-sm tracking-widest px-3 py-2 transition-colors">
                ✓ COMPLETE
              </button>
              <button onClick={e => { e.stopPropagation(); onProblem(run); }}
                className="flex items-center gap-1.5 bg-red-600/20 hover:bg-red-600/40 border border-red-500/30 text-red-400 font-bebas text-sm tracking-widest px-3 py-2 transition-colors">
                ⚠ PROBLEM
              </button>
            </>
          )}
          {isCompleted && <span className="font-inter text-xs text-green-400 font-semibold">✓ {run.quantity_completed} units done</span>}
          {hasProblem && <span className="font-inter text-xs text-red-400 font-semibold">⚠ Problem reported</span>}
          {expanded ? <ChevronUp size={14} className="text-zinc-500" /> : <ChevronDown size={14} className="text-zinc-500" />}
        </div>
      </button>
      {expanded && (
        <div className="border-t border-white/10 p-5 space-y-4 bg-[#141414]">
          {run.nfa_item && (
            <div className="bg-orange-500/10 border border-orange-500/30 px-4 py-2.5 flex items-start gap-2">
              <Shield size={13} className="text-orange-500 flex-shrink-0 mt-0.5" />
              <p className="font-inter text-xs text-orange-300 font-semibold">NFA: Serial number marking required before leaving the building. Confirm marking meets ATF spec.</p>
            </div>
          )}
          {profile?.material_spec && (
            <div className="bg-[#1e1e1e] border border-white/5 p-4 flex items-center justify-between">
              <div>
                <p className="font-inter text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Step 1: Pull Material</p>
                <div className="font-inter text-base font-semibold text-white">{profile.material_diameter_in}" OD × {matLen}" per unit</div>
                <div className="font-inter text-sm text-zinc-400">{profile.material_spec}</div>
                <div className="font-inter text-xs text-yellow-400 mt-1">+10% overage = {((matLen * run.quantity_planned) * 1.1 / 12).toFixed(2)} ft recommended</div>
                {profile.material_notes && <div className="font-inter text-[11px] text-yellow-400 mt-1">⚠ {profile.material_notes}</div>}
              </div>
              <div className="font-bebas text-4xl text-orange-500 tracking-wider">{totalFt} ft</div>
            </div>
          )}
          {operations.length > 0 && (
            <div>
              <p className="font-inter text-[10px] text-zinc-500 uppercase tracking-wider mb-2">Step 2: Operations</p>
              <div className="space-y-1.5">
                {operations.map((op, i) => (
                  <label key={i} className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={!!opsChecked[i]} onChange={e => setOpsChecked(p => ({...p, [i]: e.target.checked}))} className="mt-0.5 accent-orange-500 w-4 h-4 flex-shrink-0" />
                    <span className={`font-inter text-xs leading-relaxed ${opsChecked[i] ? 'line-through text-zinc-600' : op.startsWith('VERIFY') || op.startsWith('NFA') ? 'text-orange-300 font-semibold' : 'text-zinc-300'}`}>
                      {String(i+1).padStart(2,'0')}. {op}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}
          {profile?.setup_notes && (
            <div className="bg-blue-500/10 border border-blue-500/20 px-4 py-2.5 font-inter text-xs text-blue-300">
              <strong>Shop Note:</strong> {profile.setup_notes}
            </div>
          )}
          {isCompleted && (
            <div className="bg-green-500/10 border border-green-500/20 px-4 py-2.5 font-inter text-xs text-green-400">
              <strong>Completed:</strong> {run.quantity_completed} units by {run.completed_by} at {run.completed_at ? new Date(run.completed_at).toLocaleTimeString() : '—'}
              {run.notes && ` — "${run.notes}"`}
            </div>
          )}
          {hasProblem && <div className="bg-red-500/10 border border-red-500/20 px-4 py-2.5 font-inter text-xs text-red-400"><strong>Problem:</strong> {run.problem_reason}{run.notes && ` — "${run.notes}"`}</div>}
        </div>
      )}
    </div>
  );
}

// ── Cut Sheet History Row ─────────────────────────────────────────────────────
function HistoryRow({ plan, onLoadRuns, runsShown, allRuns }) {
  const cfg = RUN_STATUS_CFG[plan.run_status] || RUN_STATUS_CFG.no_runs;
  const pct = plan.runs_total > 0 ? Math.round((plan.runs_completed / plan.runs_total) * 100) : 0;
  const expanded = runsShown === plan.plan_id;
  return (
    <div className="border border-white/10 bg-[#1a1a1a]">
      <button className="w-full flex items-center gap-4 px-5 py-3 text-left hover:bg-white/2 transition-colors" onClick={() => onLoadRuns(expanded ? null : plan.plan_id)}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="font-inter text-sm font-semibold text-white">{plan.plan_id}</span>
            <span className={`font-inter text-[10px] font-bold px-2 py-0.5 border uppercase tracking-wider ${cfg.cls}`}>{cfg.label}</span>
            {plan.has_nfa_items && <span className="font-inter text-[10px] text-orange-400">[NFA]</span>}
            {plan.finalized && <span className="font-inter text-[10px] text-zinc-500 border border-zinc-700 px-1.5 py-0.5">Finalized</span>}
          </div>
          <div className="flex items-center gap-4 mt-0.5">
            <span className="font-inter text-xs text-zinc-500">{plan.generated_at ? new Date(plan.generated_at).toLocaleDateString() : '—'}</span>
            {plan.runs_total > 0 && (
              <>
                <span className="font-inter text-xs text-zinc-600">·</span>
                <span className="font-inter text-xs text-zinc-500">{plan.runs_completed}/{plan.runs_total} runs · {plan.total_items} units</span>
                {plan.runs_problems > 0 && <span className="font-inter text-xs text-red-400">{plan.runs_problems} problem{plan.runs_problems > 1 ? 's' : ''}</span>}
              </>
            )}
          </div>
        </div>
        {plan.runs_total > 0 && (
          <div style={{ width: 80 }} className="flex-shrink-0">
            <div className="h-1.5 bg-[#27272a] overflow-hidden">
              <div className={`h-full transition-all ${pct === 100 ? 'bg-green-500' : pct > 0 ? 'bg-orange-500' : 'bg-zinc-700'}`} style={{ width: `${pct}%` }} />
            </div>
            <div className="font-inter text-[10px] text-zinc-600 mt-0.5 text-center">{pct}%</div>
          </div>
        )}
        {expanded ? <ChevronUp size={14} className="text-zinc-500 flex-shrink-0" /> : <ChevronDown size={14} className="text-zinc-500 flex-shrink-0" />}
      </button>
      {expanded && allRuns.length > 0 && (
        <div className="border-t border-white/10 divide-y divide-white/5">
          {allRuns.map(r => (
            <div key={r.run_id} className="flex items-center justify-between px-5 py-2.5">
              <div>
                <span className="font-inter text-sm text-zinc-300">{r.product_name}</span>
                {r.nfa_item && <span className="ml-2 font-inter text-[10px] text-orange-400">[NFA]</span>}
                {r.source === 'inventory_replenishment' && <span className="ml-2 font-inter text-[10px] text-cyan-400">[Shelf]</span>}
              </div>
              <div className="flex items-center gap-3">
                <span className="font-inter text-xs text-zinc-500">×{r.quantity_planned}</span>
                <span className={`font-inter text-[10px] font-bold px-2 py-0.5 border uppercase tracking-wider ${
                  r.status === 'completed' ? 'text-green-400 bg-green-500/10 border-green-500/20' :
                  r.status === 'problem' ? 'text-red-400 bg-red-500/10 border-red-500/20' :
                  r.status === 'in_production' ? 'text-blue-400 bg-blue-500/10 border-blue-500/20' :
                  'text-zinc-500 bg-zinc-800 border-zinc-700'
                }`}>{r.status}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function AdminShopFloor() {
  const { token } = useAdminAuth();
  const [activeRuns, setActiveRuns] = useState([]);
  const [completedRuns, setCompletedRuns] = useState([]);
  const [problemRuns, setProblemRuns] = useState([]);
  const [profiles, setProfiles] = useState({});
  const [history, setHistory] = useState([]);
  const [notes, setNotes] = useState('');
  const [notesSaved, setNotesSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expandedPlan, setExpandedPlan] = useState(null);
  const [planRuns, setPlanRuns] = useState([]);
  const [tab, setTab] = useState('active');
  const [completingRun, setCompletingRun] = useState(null);
  const [completeQty, setCompleteQty] = useState(1);
  const [completeNotes, setCompleteNotes] = useState('');
  const [problemRun, setProblemRun] = useState(null);
  const [problemReason, setProblemReason] = useState(PROBLEM_REASONS[0]);
  const [problemNotes, setProblemNotes] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [allRuns, profileList, hist, notesData] = await Promise.all([
        apiFetch(token, '/api/admin/production/runs'),
        apiFetch(token, '/api/admin/machining/profiles'),
        apiFetch(token, '/api/admin/shop-floor/history'),
        apiFetch(token, '/api/admin/shop-floor/notes'),
      ]);
      const active = (allRuns || []).filter(r => r.status === 'in_production');
      const done = (allRuns || []).filter(r => r.status === 'completed');
      const probs = (allRuns || []).filter(r => r.status === 'problem');
      setActiveRuns(active);
      setCompletedRuns(done.slice(0, 20));
      setProblemRuns(probs);
      const pMap = {};
      (profileList || []).forEach(p => { pMap[p.category] = p; });
      setProfiles(pMap);
      setHistory(hist || []);
      setNotes(notesData?.notes || '');
      // Auto-switch to active tab if there are active runs
      if (active.length > 0) setTab('active');
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [token]);

  const loadPlanRuns = async (planId) => {
    setExpandedPlan(planId);
    if (!planId) { setPlanRuns([]); return; }
    const runs = await apiFetch(token, `/api/admin/production/runs`);
    setPlanRuns((runs || []).filter(r => r.plan_id === planId));
  };

  const saveNotes = async () => {
    await apiFetch(token, '/api/admin/shop-floor/notes', { method: 'PUT', body: JSON.stringify({ notes }) });
    setNotesSaved(true);
    setTimeout(() => setNotesSaved(false), 2000);
  };

  const submitComplete = async () => {
    try {
      await apiFetch(token, `/api/admin/production/runs/${completingRun.run_id}/complete`, {
        method: 'PUT', body: JSON.stringify({ quantity_completed: completeQty, notes: completeNotes })
      });
      setCompletingRun(null);
      load();
    } catch (e) { alert(e.message); }
  };

  const submitProblem = async () => {
    try {
      await apiFetch(token, `/api/admin/production/runs/${problemRun.run_id}/problem`, {
        method: 'PUT', body: JSON.stringify({ problem_reason: problemReason, notes: problemNotes })
      });
      setProblemRun(null);
      load();
    } catch (e) { alert(e.message); }
  };

  const handleCompleteAll = async () => {
    if (!window.confirm(`Mark ALL ${activeRuns.length} run(s) complete and move orders to Serialization?`)) return;
    try {
      await apiFetch(token, '/api/admin/shop-floor/complete-all', { method: 'POST' });
      window.location.href = '/admin/serialization';
    } catch (e) { alert(e.message); }
  };

  const TABS = [
    { key: 'active', label: `Active Runs (${activeRuns.length})`, urgent: activeRuns.length > 0 },
    { key: 'history', label: `Cut Sheet History (${history.length})` },
    { key: 'completed', label: `Completed Runs (${completedRuns.length})` },
    { key: 'materials', label: 'Material Notes' },
  ];

  return (
    <div data-testid="admin-shop-floor-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-bebas text-4xl text-white tracking-wider">SHOP FLOOR</h1>
          <p className="font-inter text-sm text-zinc-500 mt-1">Active runs · cut sheet history · material tracking</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="font-inter text-xs border border-white/10 hover:border-white/30 text-zinc-500 hover:text-white px-3 py-2 transition-colors">
            <RefreshCw size={13} />
          </button>
          <Link to="/admin/production" className="font-inter text-xs text-zinc-500 hover:text-orange-400 border border-white/10 hover:border-orange-500/30 px-3 py-2 transition-colors">← Cut Sheet Engine</Link>
        </div>
      </div>

      {/* Stats strip — always visible */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {[
          { val: activeRuns.length, label: 'Active Runs', color: activeRuns.length > 0 ? 'text-blue-400' : 'text-zinc-600', href: null },
          { val: problemRuns.length, label: 'Problems', color: problemRuns.length > 0 ? 'text-red-400' : 'text-zinc-600', href: null },
          { val: completedRuns.length, label: 'Completed (recent)', color: 'text-green-400', href: null },
          { val: history.filter(p => p.run_status === 'completed').length, label: 'Finished Cut Sheets', color: 'text-teal-400', href: null },
        ].map(s => (
          <div key={s.label} className="bg-[#1a1a1a] border border-white/10 p-4 text-center">
            <div className={`font-bebas text-3xl tracking-wider ${s.color}`}>{s.val}</div>
            <div className="font-inter text-[10px] text-zinc-500 uppercase tracking-wider mt-0.5 leading-tight">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 border-b border-white/10">
        {TABS.map(t => (
          <button key={t.key} data-testid={`floor-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 font-inter text-sm px-4 py-2.5 transition-colors border-b-2 -mb-px ${tab === t.key ? 'border-orange-500 text-white' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
            {t.label}
            {t.urgent && <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48"><div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <>
          {/* ── TAB: ACTIVE RUNS ─────────────────────────────────────────── */}
          {tab === 'active' && (
            <div data-testid="floor-active-tab">
              {problemRuns.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2"><AlertTriangle size={15} className="text-red-400" /><span className="font-bebas text-lg text-red-400 tracking-wider">PROBLEMS ({problemRuns.length})</span></div>
                  <div className="space-y-2">{problemRuns.map(r => <RunCard key={r.run_id} run={r} profile={profiles[r.category]} onComplete={run => { setCompletingRun(run); setCompleteQty(run.quantity_planned); setCompleteNotes(''); }} onProblem={run => { setProblemRun(run); setProblemReason(PROBLEM_REASONS[0]); setProblemNotes(''); }} />)}</div>
                </div>
              )}
              {activeRuns.length > 0 ? (
                <>
                  <div className="space-y-3 mb-5">
                    {activeRuns.map(r => <RunCard key={r.run_id} run={r} profile={profiles[r.category]} onComplete={run => { setCompletingRun(run); setCompleteQty(run.quantity_planned); setCompleteNotes(''); }} onProblem={run => { setProblemRun(run); setProblemReason(PROBLEM_REASONS[0]); setProblemNotes(''); }} />)}
                  </div>
                  <div className="bg-[#111111] border border-green-500/30 p-5 flex items-center justify-between">
                    <div>
                      <p className="font-bebas text-lg text-white tracking-wider">ALL MACHINING DONE?</p>
                      <p className="font-inter text-xs text-zinc-400">Click to mark all runs complete and move orders to the Serialization queue.</p>
                    </div>
                    <button onClick={handleCompleteAll} className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-bebas text-base tracking-widest px-6 py-3 transition-colors whitespace-nowrap ml-4">
                      ✓ ALL COMPLETE → SERIALIZATION
                    </button>
                  </div>
                </>
              ) : (
                <div className="text-center py-14 bg-[#1a1a1a] border border-white/10">
                  <PlayCircle size={40} className="text-zinc-700 mx-auto mb-3" />
                  <p className="font-bebas text-2xl text-zinc-500 tracking-wider mb-2">NO ACTIVE RUNS</p>
                  <p className="font-inter text-sm text-zinc-600 mb-5">Finalize a cut sheet to send work to the floor.</p>
                  <Link to="/admin/production" className="font-bebas text-sm tracking-widest bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 transition-colors">
                    → GO TO CUT SHEET ENGINE
                  </Link>
                </div>
              )}
            </div>
          )}

          {/* ── TAB: CUT SHEET HISTORY ────────────────────────────────────── */}
          {tab === 'history' && (
            <div data-testid="floor-history-tab">
              <div className="flex items-center gap-3 mb-4">
                <h2 className="font-bebas text-2xl text-white tracking-wider">CUT SHEET HISTORY</h2>
                <span className="font-inter text-xs text-zinc-500">Click a plan to see its runs. Color = completion status.</span>
              </div>
              {/* Legend */}
              <div className="flex flex-wrap gap-3 mb-4">
                {Object.entries(RUN_STATUS_CFG).map(([key, cfg]) => (
                  <span key={key} className={`font-inter text-[10px] font-bold px-2 py-1 border uppercase tracking-wider ${cfg.cls}`}>{cfg.label}</span>
                ))}
              </div>
              {history.length === 0 ? (
                <div className="text-center py-12 bg-[#1a1a1a] border border-white/10">
                  <p className="font-inter text-sm text-zinc-600">No cut sheets generated yet. Generate one from the Cut Sheet Engine.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {history.map(plan => (
                    <HistoryRow key={plan.plan_id} plan={plan} onLoadRuns={loadPlanRuns}
                      runsShown={expandedPlan} allRuns={expandedPlan === plan.plan_id ? planRuns : []} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── TAB: COMPLETED RUNS ───────────────────────────────────────── */}
          {tab === 'completed' && (
            <div data-testid="floor-completed-tab">
              <h2 className="font-bebas text-2xl text-white tracking-wider mb-4">RECENTLY COMPLETED RUNS</h2>
              {completedRuns.length === 0 ? (
                <div className="text-center py-12 bg-[#1a1a1a] border border-white/10">
                  <CheckCircle size={36} className="text-zinc-700 mx-auto mb-3" />
                  <p className="font-inter text-sm text-zinc-600">No completed runs yet.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {completedRuns.map(r => (
                    <RunCard key={r.run_id} run={r} profile={profiles[r.category]}
                      onComplete={() => {}} onProblem={() => {}} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── TAB: MATERIAL NOTES ───────────────────────────────────────── */}
          {tab === 'materials' && (
            <div data-testid="floor-materials-tab">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="font-bebas text-2xl text-white tracking-wider">MATERIAL NOTES & INVENTORY</h2>
                  <p className="font-inter text-xs text-zinc-500 mt-0.5">Track available bar stock, partial pieces, remnants from previous runs</p>
                </div>
                <button onClick={saveNotes}
                  className={`flex items-center gap-2 font-bebas text-sm tracking-widest px-4 py-2.5 transition-colors ${notesSaved ? 'bg-green-600 text-white' : 'bg-orange-500 hover:bg-orange-600 text-white'}`}>
                  <Save size={13} />{notesSaved ? '✓ SAVED' : 'SAVE NOTES'}
                </button>
              </div>
              {/* Quick reference: material specs from profiles */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                {Object.values(profiles).filter(p => p.material_spec).map(p => (
                  <div key={p.category} className="bg-[#1a1a1a] border border-white/10 px-4 py-3">
                    <div className="font-inter text-[10px] text-zinc-500 uppercase tracking-wider mb-1">{p.display_name || p.category}</div>
                    <div className="font-inter text-sm font-semibold text-white">{p.material_diameter_in}" OD — {p.material_spec?.split(',')[0]}</div>
                    <div className="font-inter text-xs text-zinc-500 mt-0.5">{p.material_length_per_unit_in}" per unit · min batch = {p.production_batch_size || 5} units</div>
                  </div>
                ))}
              </div>
              <div>
                <label className="block font-inter text-xs text-zinc-500 uppercase tracking-wider mb-2">
                  Notes (remnant pieces, available stock, special material notes for next run)
                </label>
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  rows={14}
                  className="w-full bg-[#1e1e1e] border border-white/10 focus:border-orange-500 text-white font-inter text-sm px-4 py-3 outline-none transition-colors resize-none placeholder-zinc-600 leading-relaxed"
                  placeholder={`Examples:
• 2.5 ft of 1.5" 316L SS remaining from batch #7 — on shelf A3
• 3/4 bar of 1.0" 17-4PH — good for ~4 more muzzle brakes, shelf B1
• Order more 316L SS — getting low (< 5 ft)
• Canooter Valve run: check tooling before starting, insert #3 was getting worn
• Note from Apr 5: had one Naughty 9 bore slightly undersized at 0.352", re-run at 0.354"`}
                />
              </div>
            </div>
          )}
        </>
      )}

      {/* Complete Modal */}
      {completingRun && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-[#111111] border border-white/10 w-full max-w-md p-6">
            <h3 className="font-bebas text-2xl text-white tracking-wider mb-1">MARK RUN COMPLETE</h3>
            <p className="font-inter text-sm text-zinc-400 mb-5">{completingRun.product_name}</p>
            <div className="mb-4">
              <label className="block font-inter text-xs text-zinc-500 uppercase tracking-wider mb-2">Units completed (planned: {completingRun.quantity_planned})</label>
              <input type="number" min="1" value={completeQty} onChange={e => setCompleteQty(+e.target.value)}
                className="w-full bg-[#1e1e1e] border border-white/10 focus:border-green-500 text-white font-bebas text-3xl px-4 py-4 outline-none text-center" />
            </div>
            <textarea rows={2} value={completeNotes} onChange={e => setCompleteNotes(e.target.value)} placeholder="Notes..."
              className="w-full bg-[#1e1e1e] border border-white/10 text-white font-inter text-sm px-3 py-2 outline-none resize-none placeholder-zinc-600 mb-4" />
            {completingRun.source === 'inventory_replenishment' && (
              <div className="bg-cyan-500/10 border border-cyan-500/20 p-3 mb-4 font-inter text-xs text-cyan-300">
                <strong>Shelf stock:</strong> {completeQty} serials auto-generated → added to manufactured inventory.
              </div>
            )}
            <div className="flex gap-3">
              <button onClick={submitComplete} className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bebas text-xl py-3 transition-colors">✓ CONFIRM</button>
              <button onClick={() => setCompletingRun(null)} className="flex-1 border border-white/20 text-zinc-400 font-bebas text-xl py-3 transition-colors">CANCEL</button>
            </div>
          </div>
        </div>
      )}

      {/* Problem Modal */}
      {problemRun && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-[#111111] border border-red-500/30 w-full max-w-md p-6">
            <h3 className="font-bebas text-2xl text-red-400 tracking-wider mb-1">⚠ REPORT PROBLEM</h3>
            <p className="font-inter text-sm text-zinc-400 mb-5">{problemRun.product_name}</p>
            <div className="mb-4">
              <label className="block font-inter text-xs text-zinc-500 uppercase tracking-wider mb-2">What happened?</label>
              <select value={problemReason} onChange={e => setProblemReason(e.target.value)}
                className="w-full bg-[#1e1e1e] border border-white/10 text-white font-inter text-sm px-3 py-2.5 outline-none">
                {PROBLEM_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <textarea rows={3} value={problemNotes} onChange={e => setProblemNotes(e.target.value)} placeholder="Details..."
              className="w-full bg-[#1e1e1e] border border-white/10 text-white font-inter text-sm px-3 py-2 outline-none resize-none placeholder-zinc-600 mb-4" />
            <div className="flex gap-3">
              <button onClick={submitProblem} className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bebas text-xl py-3 transition-colors">REPORT</button>
              <button onClick={() => setProblemRun(null)} className="flex-1 border border-white/20 text-zinc-400 font-bebas text-xl py-3 transition-colors">CANCEL</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
